/**
 * @fileoverview Table Head component.
 * Child component of Table used to render the <thead> and its contents.
 * @author Derek Seo (dseo@yext.com)
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.table.tablehead');

import {Checkbox} from '/ui/components/checkbox/checkbox';
import {SortDirection, toggleSortDirection, CheckboxState} from '/ui/components/table/common';
import * as Types from '/ui/components/table/types';

import * as styles from '/ui/components/table/head/tablehead.module.scss';
import AscendingSortIcon from '/ui/components/table/icons/ascending-sort.svg';
import DescendingSortIcon from '/ui/components/table/icons/descending-sort.svg';

/**
 * @typedef {{
 *   columns: !Array<!Types.Column>,
 *   controlsEnabled: boolean,
 *   selectHeaderState: (!Types.CheckboxState | undefined),
 *   onSelectHeader: ((function(!Types.CheckboxState): void) | undefined),
 *   isLoading: (boolean | undefined),
 * }}
 */
let TableHeadProps;

/**
 * @param {!TableHeadProps} props
 * @return {React.ReactElement}
 */
export function TableHead(props) {
  const {columns} = props;

  const headerCells = columns.map(column => columnHeaderCell(column, props));
  return (
    <thead>
      <tr className={styles.row}>
        {headerCells}
      </tr>
    </thead>
  );
}

/**
 * @param {!Types.Column} column
 * @param {!TableHeadProps} tableHeadProps
 * @returns {React.ReactElement}
 */
function columnHeaderCell(column, {
  controlsEnabled,
  selectHeaderState,
  onSelectHeader,
  isLoading,
}) {
  const hasSort = column.onSort !== undefined;
  let cellContents;
  if (hasSort && controlsEnabled) {
    cellContents = (
      <button
        className={`${styles.cellContents} ${styles.sortButton}`}
        type="button"
        onClick={() => {
          column = /** @type {Types.SortableColumn} */ (column);
          column.onSort(column.id, toggleSortDirection(column.sortDirection));
        }}
      >
        <span>{column.label}</span>
        {sortIcon(column)}
      </button>
    );
  } else if (column.isSelect && selectHeaderState !== undefined && onSelectHeader) {
    cellContents = (
      <div className={styles.selectCell}>
        <Checkbox
          disabled={isLoading}
          indeterminate={selectHeaderState === CheckboxState.INDETERMINATE}
          checked={selectHeaderState === CheckboxState.SELECTED}
          onChange={() => onSelectHeader(/** @type {Types.CheckboxState} */(selectHeaderState))}
          noMargin />
      </div>
    );
  } else {
    cellContents = (
      <div className={styles.cellContents}>
        <span>{column.label}</span>
      </div>
    );
  }
  let ariaSort;
  switch (column.sortDirection || undefined) {
    case SortDirection.ASCENDING:
      ariaSort = 'ascending';
      break;
    case SortDirection.DESCENDING:
      ariaSort = 'descending';
      break;
    default:
      ariaSort = 'none';
  }
  return (
    <th
      className={styles.cell}
      key={column.id}
      scope="col"
      aria-sort={ariaSort}
    >
      {cellContents}
    </th>
  );
}

/**
 *
 * @param {!Types.Column} column
 * @returns {React.ReactElement}
 */
function sortIcon(column) {
  if (!(column.onSort && column.sortDirection)) {
    return <span className={styles.sortIcon}></span>; // Do this for spacing
  }
  const SortIcon = column.sortDirection === SortDirection.ASCENDING ? AscendingSortIcon : DescendingSortIcon;
  return (
    <SortIcon
      className={styles.sortIcon}
    />
  );
}
